<script setup>
import { computed } from 'vue';
import locales from 'shared/constants/locales';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  targetLanguage: { type: String, default: '' },
  preview: { type: Object, default: null },
  isLoading: { type: Boolean, default: false },
  error: { type: String, default: '' },
});

const emit = defineEmits(['update:preview', 'send', 'sendOriginal', 'cancel']);

const languageName = computed(() => {
  const code = props.targetLanguage;
  const base = String(code).split(/[-_]/)[0];
  return locales[code] || locales[base] || code;
});

// The preview is editable: a translation the agent cannot correct is worse
// than no translation, because they cannot see what they are unable to fix.
const previewContent = computed({
  get: () => props.preview?.content ?? '',
  set: value => emit('update:preview', { ...props.preview, content: value }),
});
</script>

<template>
  <div
    class="flex flex-col gap-2 px-3 py-2 mb-1 border rounded-lg border-n-weak bg-n-alpha-1"
  >
    <div class="flex items-center justify-between gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('CONVERSATION.TRANSLATION.PREVIEW_TITLE', { language: languageName }) }}
      </span>
      <span v-if="isLoading" class="text-xs text-n-slate-11">
        {{ $t('CONVERSATION.TRANSLATION.TRANSLATING') }}
      </span>
    </div>

    <p v-if="error" class="text-xs text-n-ruby-11">{{ error }}</p>

    <textarea
      v-else-if="preview"
      v-model="previewContent"
      rows="4"
      class="w-full !mb-0 text-sm resize-y !bg-n-background !border-n-weak"
    />

    <p class="text-xs text-n-slate-11">
      {{ $t('CONVERSATION.TRANSLATION.PREVIEW_HELP') }}
    </p>

    <div class="flex items-center gap-2">
      <NextButton
        sm
        solid
        blue
        :label="$t('CONVERSATION.TRANSLATION.SEND_TRANSLATED', { language: languageName })"
        :disabled="isLoading || !preview || !previewContent.trim()"
        @click="emit('send')"
      />
      <NextButton
        sm
        ghost
        slate
        :label="$t('CONVERSATION.TRANSLATION.SEND_ORIGINAL')"
        :disabled="isLoading"
        @click="emit('sendOriginal')"
      />
      <NextButton
        sm
        ghost
        slate
        :label="$t('CONVERSATION.TRANSLATION.CANCEL')"
        @click="emit('cancel')"
      />
    </div>
  </div>
</template>
